/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import {
  LayoutDashboard,
  FileText,
  Building2,
  Users,
  Calendar,
  Megaphone,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Home,
  MapPin,
  Building,
  UsersRound,
  Shield,
  ScrollText,
  ClipboardList,
  Mail,
  CalendarDays,
  UserX,
  UserCheck,
  BarChart3,
  FileWarning,
} from 'lucide-react';

const NAV_ITEMS = [
  { href: '/panel', label: 'Kontrol Paneli', icon: LayoutDashboard, permission: 'dashboard.view' },
  { href: '/panel/organlar', label: 'Organ Yapisi', icon: Building, permission: 'organs.view' },
  { href: '/panel/stk', label: 'STK Basvurulari', icon: Building2, permission: 'stk.view' },
  { href: '/panel/stk/belge-talepleri', label: 'STK Belge Talepleri', icon: FileText, roles: ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'] },
  { href: '/panel/muhtar-basvurulari', label: 'Muhtar Basvurulari', icon: UserCheck, roles: ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'] },
  { href: '/panel/calisma-gruplari', label: 'Meclis ve Calisma Gruplari', icon: UsersRound, permission: 'wg.view' },
  { href: '/panel/talepler', label: 'Vatandas Katilim', icon: FileText, permission: 'tickets.view' },
  { href: '/panel/toplantilar', label: 'Toplantilar', icon: Calendar, permission: 'meetings.view' },
  { href: '/panel/duyurular', label: 'Duyurular', icon: Megaphone, permission: 'announcements.view' },
  { href: '/panel/anketler', label: 'Anketler', icon: ClipboardList, permission: 'surveys.view' },
  { href: '/panel/etkinlikler', label: 'Etkinlikler', icon: CalendarDays, permission: 'events.view' },
  { href: '/panel/kullanicilar', label: 'Kullanicilar', icon: Users, permission: 'users.view' },
  { href: '/panel/roller', label: 'Rol Yonetimi', icon: Shield, permission: 'roles.view' },
  { href: '/panel/loglar', label: 'Sistem Loglari', icon: ScrollText, permission: 'logs.view' },
  { href: '/panel/smtp-ayarlari', label: 'E-posta Ayarlari', icon: Mail, permission: 'settings.view' },
  { href: '/panel/silinmis-hesaplar', label: 'Silinmis Hesaplar', icon: UserX, roles: ['admin', 'kent_konseyi_baskani'] },
  { href: '/panel/veri-ihlali', label: 'KVKK Veri Ihlali', icon: FileWarning, roles: ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'] },
  { href: '/panel/istatistikler', label: 'Istatistikler', icon: BarChart3, roles: ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'] },
];

// Default visible items for vatandas role (even if permissions haven't loaded yet)
const VATANDAS_DEFAULT_ITEMS = [
  '/panel', '/panel/talepler', '/panel/duyurular', '/panel/toplantilar',
  '/panel/anketler', '/panel/calisma-gruplari', '/panel/etkinlikler',
];

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, hasPermission } = useAuth();

  const navItems = NAV_ITEMS.filter(item => {
    // Role-based items (no permission needed)
    if (item.roles) return item.roles.includes(user?.role);
    // If user has the permission via RBAC, show it
    if (hasPermission(item.permission)) return true;
    // Fallback: for vatandas role, always show default items even if permissions haven't loaded
    if (user?.role === 'vatandas' && VATANDAS_DEFAULT_ITEMS.includes(item.href)) return true;
    // For stk role, show STK + default vatandas items
    if (user?.role === 'stk' && (item.href === '/panel/stk' || VATANDAS_DEFAULT_ITEMS.includes(item.href))) return true;
    // For muhtar role, show default vatandas items
    if (user?.role === 'muhtar' && VATANDAS_DEFAULT_ITEMS.includes(item.href)) return true;
    return false;
  });

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-64 bg-slate-950 text-white transition-transform duration-300 lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <Link to="/panel" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-white">Kent Konseyi</h1>
                <p className="text-xs text-slate-400">Dijital Katılım</p>
              </div>
            </Link>
            <button
              onClick={onClose}
              className="lg:hidden p-2 hover:bg-slate-900 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onClose}
                  data-testid={`sidebar-nav-${item.href.replace('/panel/', '').replace('/', '-') || 'dashboard'}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-blue-600 text-white"
                      : "text-slate-400 hover:bg-slate-900 hover:text-white hover:translate-x-1"
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden">
                {user?.avatar_url ? (
                  <img src={`${API_URL}${user.avatar_url}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-4 h-4 text-slate-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name} {user?.surname}
                </p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const Header = ({ onMenuClick, unreadNotifications }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/giris');
  };

  const getRoleBadge = (role) => {
    const roleLabels = {
      admin: 'Yönetici',
      genel_sekreter: 'Genel Sekreter',
      kent_konseyi_baskani: 'Başkan',
      meclis_baskani: 'Meclis Başkanı',
      muhtar: 'Muhtar',
      stk: 'STK',
      vatandas: 'Vatandaş',
    };
    return roleLabels[role] || role;
  };

  return (
    <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 px-4 lg:px-6">
      <div className="flex items-center justify-between h-full">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="hidden sm:flex items-center gap-2 text-sm text-slate-500">
            <MapPin className="w-4 h-4" />
            <span>Adana Kent Konseyi</span>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Notifications */}
          <Link
            to="/panel/bildirimler"
            className="relative p-2 hover:bg-slate-100 rounded-lg"
            data-testid="notifications-btn"
          >
            <Bell className="w-5 h-5 text-slate-600" />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </Link>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 p-2 hover:bg-slate-100 rounded-lg"
                data-testid="user-menu-btn"
              >
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center overflow-hidden">
                  {user?.avatar_url ? (
                    <img src={`${API_URL}${user.avatar_url}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm font-medium text-white">
                      {user?.name?.charAt(0)}{user?.surname?.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-slate-900">
                    {user?.name} {user?.surname}
                  </p>
                  <Badge variant="secondary" className="text-xs">
                    {getRoleBadge(user?.role)}
                  </Badge>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name} {user?.surname}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/panel/profil" className="cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Profilim
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/panel/ayarlar" className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Ayarlar
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış Yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadNotifications] = useState(3); // This would come from API

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:pl-64">
        <Header
          onMenuClick={() => setSidebarOpen(true)}
          unreadNotifications={unreadNotifications}
        />
        
        <main className="p-4 sm:p-6 lg:p-8 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
